/* eslint-disable import/no-named-as-default-member */
import create from 'zustand';
import { persist, devtools, subscribeWithSelector } from 'zustand/middleware';
import tradeAPI from 'lib/api/trade';
import orderbook from 'lib/api/orderbook';
import securities from 'lib/api/securities';
import {
  TransactionHistoryParams,
  TransactionHistoryResponse,
  TransactionHistoryResponseBody,
} from 'global/Securities/types/api/history';
import orderTableStore from 'features/trade/components/OrderTable/store';
import {
  generateUpdatedPortfolioItem,
  generateUpdatedPortfolioHeader,
} from 'global/Securities/utils/formula';
import Transform from 'utils/securities/transformData';
import VTTransfrom from 'utils/securities/transformVirtualTradingData';

import type {
  ListItem,
  PortfolioState,
  PortfolioStateItem,
} from 'global/Securities/types/store/portfolio';
import type {
  OrderState,
  OrderStateItem as OrderItem,
} from 'global/Securities/types/store/order';
import { TradingFormula } from 'global/Securities/types/store/formula';
import { getVirtualOrderList } from 'global/VirtualTrading/api/order';
import { getPortfolioList } from 'global/VirtualTrading/api/portfolio';

interface iDefaultData<T = any> {
  isLoading?: boolean;
  message?: string;
  data?: T;
  error?: string;
}

const defaultData = {
  isLoading: false,
  message: '',
  data: null,
  error: '',
};

interface iGetHistory extends iDefaultData {
  showSkeleton: boolean;
}

interface TransactionHistoryDefaultData
  extends iDefaultData<TransactionHistoryResponseBody> {
  showSkeleton: boolean;
}

type PortfolioDefaultData = iDefaultData<Partial<PortfolioState>>;
type OrderHistoryDefaultData = iDefaultData<OrderState>;
type ConfigFormulaDefaultData = iDefaultData<TradingFormula>;

interface iState {
  portfolio: PortfolioDefaultData;
  orderHistory: OrderHistoryDefaultData;
  configFormula: ConfigFormulaDefaultData;
  exerciseList: iDefaultData;
  history: iGetHistory;
  transactionHistory: TransactionHistoryDefaultData;
  company: iDefaultData;
  corpActions: iDefaultData;
  exercise: iDefaultData;
  cashOnHand: iDefaultData;
  portfolioDetail: iDefaultData;
  portfolioDetailDayTrade: iDefaultData;
  rightTradeable: iDefaultData;
  stockTradable: iDefaultData;
}

const historyDefault = {
  ...defaultData,
  data: {
    data: [],
    page: '1',
    isNeedToFetchMore: false,
    totalRealizedAmount: 0,
    tooltipHeader: '',
    tooltips: [],
  },
  showSkeleton: true,
};

const transactionHistoryDefault: TransactionHistoryDefaultData = {
  ...defaultData,
  data: {
    history: [],
  },
  showSkeleton: false,
};

const portfolioDefault = {
  ...defaultData,
  data: {
    tradingbalance: 0,
    amount_invested: 0,
    amount_allocated: 0,
    unrealised_profitloss: 0,
    gain: 0,
    equity: 0,
    result: [],
    auto_order_info: '',

    // Day Trade
    day_trade: [],
    debt_ratio: 0,
    debt_total: 0,
    debt_market_value: 0,
  },
};

const portfolioDetailDefault = {
  ...defaultData,
  data: {
    available_lot: 0,
    available_share: 0,
    exercise_data: {
      price: 0,
    },
  },
};

const cashOnHandDefault = {
  ...defaultData,
  data: {
    available_cash_on_hand: 0,
  },
};

const orderHistoryDefault = {
  ...defaultData,
  data: [],
};

interface ITokenStore extends iState {
  setSkeleton: (key: keyof iState, showSkeleton: boolean) => void;
  getPortfolio: (isReal: boolean, arg?: boolean) => Promise<iDefaultData>;
  getOrderHistory: (isReal: boolean, arg?: boolean) => Promise<iDefaultData>;
  getConfigFormula: () => Promise<iDefaultData>;
  getExerciseList: () => Promise<iDefaultData>;
  getHistory: (any) => Promise<iDefaultData>;
  getTransactionHistory: (
    urlParams: TransactionHistoryParams,
  ) => Promise<TransactionHistoryResponse | iDefaultData>;
  getCompany: (any) => Promise<iDefaultData>;
  getCorpActions: (any) => Promise<iDefaultData>;
  getExercise: (any) => Promise<iDefaultData>;
  getPortfolioDetail: (symbol: string) => Promise<iDefaultData>;
  getCashOnHand: () => Promise<iDefaultData>;
  checkRightTradeable: () => Promise<iDefaultData>;
  resetModalData: () => void;
  resetCompany: () => void;

  updatePortfolioListItem: (
    stockCode: string,
    newData: Partial<PortfolioStateItem>,
  ) => void;

  updatePortfolioSummary: (newData: Partial<PortfolioStateItem>) => void;
  updatePortfolioList: (newData: PortfolioState) => void;
  updatePortfolioItem: (
    stockCode: string,
    newData: PortfolioStateItem,
    isDayTrade?: boolean,
  ) => void;
  deletePortfolioItem: (stockCode: string, isDayTrade?: boolean) => void;
  updateOrderList: (newData: OrderItem[]) => void;
  updateOrderListItem: (orderId: string, newData: OrderItem) => void;
  deleteOrderListItem: (orderId: string) => void;

  checkStockTradable: (stockCode: string) => Promise<iDefaultData>;
}

const setIsLoading = (key, set, custom: iDefaultData = defaultData) => {
  set(
    {
      [key]: {
        ...custom,
        isLoading: true,
      },
    },
    false,
    `setIsLoading/${key}`,
  );
};

const setError = (error, key, set, custom: iDefaultData = defaultData) => {
  set(
    {
      [key]: {
        ...custom,
        error,
      },
    },
    false,
    `setError/${key}`,
  );
};

const setData = (data, message, key, set, custom = defaultData) => {
  set(
    {
      [key]: {
        ...custom,
        data,
        message,
      },
    },
    false,
    `setData/${key}`,
  );
};

const tradingDataStore = create<ITokenStore>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => ({
        portfolio: portfolioDefault,
        orderHistory: orderHistoryDefault,
        configFormula: defaultData,
        exerciseList: defaultData,
        history: historyDefault,
        transactionHistory: transactionHistoryDefault,
        company: defaultData,
        corpActions: defaultData,
        exercise: defaultData,
        cashOnHand: cashOnHandDefault,
        portfolioDetail: portfolioDetailDefault,
        portfolioDetailDayTrade: portfolioDetailDefault,
        rightTradeable: defaultData,
        stockTradable: defaultData,

        resetModalData: () => {
          set({
            rightTradeable: defaultData,
            exercise: defaultData,
            cashOnHand: cashOnHandDefault,
          });
        },

        resetCompany: () => {
          set({ company: defaultData });
        },

        setSkeleton: (key, showSkeleton) => {
          set({
            [key]: {
              ...get()[key],
              showSkeleton,
            },
          });
        },

        getPortfolio: async (isRealTrading, resetData = true) => {
          const key = 'portfolio';
          if (resetData) setIsLoading(key, set, portfolioDefault);
          try {
            const api = !isRealTrading
              ? getPortfolioList
              : tradeAPI.getPortfolio;

            const response = await api();

            if (response.data.error || !response.data) {
              const error = 'Attempt to get portfolio failed';
              setError(error, key, set, portfolioDefault);
              throw new Error(error);
            }
            const { data: responseData, message } = response.data;
            const data = isRealTrading
              ? Transform.portfolioList(responseData)
              : VTTransfrom.vtPortfolioList(responseData);

            setData(data, message, key, set, portfolioDefault);
            return { data, message };
          } catch (error) {
            setError(error, key, set, portfolioDefault);
            return { error };
          }
        },

        getConfigFormula: async () => {
          const key = 'configFormula';
          const persistedFormula = get().configFormula;
          setIsLoading(key, set, persistedFormula);
          try {
            const response = await tradeAPI.getConfigFormula();

            if (!response.data) {
              const error = 'Attempt to retrieve config formula failed';
              setError(error, key, set);
              throw new Error(error);
            }

            const { data: responseData, message } = response.data;

            const data = Transform.formulaData(responseData);

            const formulaStockbit = {
              formula: {
                ...data.formula,
                fee_buy: data.fee.buy,
                fee_sell: data.fee.sell,
                fee_buy_day_trade: data.day_trade.fee.buy,
                fee_sell_day_trade: data.day_trade.fee.sell,
              },
            };

            localStorage.setItem(
              'formulaStockbit',
              JSON.stringify(formulaStockbit),
            );
            localStorage.setItem(
              'securitiesPreviewFeeData',
              JSON.stringify(data.preview_fee),
            );
            localStorage.setItem(
              'securitiesPreviewFeeDataDayTrade',
              JSON.stringify(data.day_trade.preview_fee),
            );

            setData(
              {
                ...formulaStockbit,
                preview_fee: data.preview_fee,
                day_trade: data.day_trade,
              },
              message,
              key,
              set,
            );
            return { data, message };
          } catch (error) {
            setError(error, key, set, persistedFormula);
            return { error };
          }
        },

        getOrderHistory: async (isRealTrading, resetData = true) => {
          const key = 'orderHistory';
          if (resetData) setIsLoading(key, set, orderHistoryDefault);
          try {
            const api = !isRealTrading
              ? getVirtualOrderList
              : tradeAPI.getOrderList;

            const response = await api(0);

            if (!response.data) {
              const error = 'Attempt to retrieve order history failed';
              setError(error, key, set, orderHistoryDefault);
              throw new Error(error);
            }
            const { data, message } = response.data;

            const result = isRealTrading
              ? Transform.orderList(data)
              : VTTransfrom.vtOrderList(data);

            setData(result, message, key, set);
            orderTableStore.getState().searchAndFilter(result);
            return { data: result, message };
          } catch (error) {
            setError(error, key, set, orderHistoryDefault);
            return { error };
          }
        },

        getExerciseList: async () => {
          const key = 'exerciseList';
          setIsLoading(key, set);
          try {
            const response = await tradeAPI.getExerciseList();

            if (!response.data) {
              const error = 'Attempt to retrieve exercise list failed';
              setError(error, key, set);
              throw new Error(error);
            }
            const { data, message } = response.data;
            setData(data, message, key, set);
            return { data, message };
          } catch (error) {
            setError(error, key, set);
            return { error };
          }
        },

        getHistory: async ({ filters, appendData }) => {
          const key = 'history';
          if (Number(filters.page) > 1) {
            const oldData = get().history;
            set({
              history: {
                ...oldData,
                isLoading: true,
              },
            });
          } else {
            setIsLoading(key, set, historyDefault);
          }
          try {
            const response = await tradeAPI.getHistory(filters);

            if (!response.data) {
              const error = 'Attempt to retrieve history list failed';
              setError(error, key, set, historyDefault);
              throw new Error(error);
            }

            const { data, message, error } = response.data;

            if (error) {
              setError(error, key, set, {
                ...historyDefault,
                // @ts-ignore
                showSkeleton: false,
              });
              throw new Error(error);
            }

            if (data) {
              const page = filters?.page || 1;
              let updatedData = [];
              const oldData = get().history.data?.data;
              if (data?.history) {
                const transformedHistoryData = data.history.reduce(
                  (prev, curr) => {
                    const isMonthlyRealizedAvailable =
                      typeof curr === 'object' && 'monthly_gain' in curr;

                    prev.push(
                      {
                        groupDate: curr.date,
                        ...(isMonthlyRealizedAvailable
                          ? { groupRealizedGain: curr.monthly_gain }
                          : { groupRealizedGain: null }),
                      },
                      ...curr.history_list,
                    );
                    return prev;
                  },
                  [],
                );

                if (
                  appendData &&
                  oldData.length &&
                  transformedHistoryData.length
                ) {
                  const isGroupDateExistOnPrevPage =
                    oldData?.find(
                      (item) =>
                        item.groupDate === transformedHistoryData[0]?.groupDate,
                    ) || null;

                  if (isGroupDateExistOnPrevPage) {
                    transformedHistoryData.shift();
                  }

                  updatedData = [...oldData, ...transformedHistoryData];
                } else {
                  updatedData = transformedHistoryData;
                }
              } else if (appendData && oldData) {
                updatedData = oldData;
              }

              const newData = {
                data: updatedData,
                page,
                isNeedToFetchMore: page < data.meta.max_page,
                totalRealizedAmount: data.meta.total_realized_amount,
                tooltipHeader: data.meta.tooltip_header,
                tooltips: data.meta.tooltips,
              };

              setData(newData, message, key, set, {
                ...historyDefault,
                // @ts-ignore
                showSkeleton: false,
              });
            }

            return { data, message };
          } catch (error) {
            setError(error, key, set, {
              ...historyDefault,
              // @ts-ignore
              showSkeleton: false,
            });
            return { error };
          }
        },

        /**
         *
         * TODO | SBPD-9819
         * Migrate to use the getHistory API
         * - [ ] Adjust the @params to match /history API
         * - [ ] Adjust the response of the /history
         * - [ ] Transform the response to the legacy UI
         * - [ ] Test
         */

        getTransactionHistory: async (urlParams) => {
          const key = 'transactionHistory';
          setIsLoading(key, set);

          try {
            const response = await tradeAPI.getHistory(urlParams);

            if (!response.data) {
              const error = 'Attempt to retrieve history list failed';
              setError(error, key, set, historyDefault);
              throw new Error(error);
            }

            const { data, message, error } = response.data;

            if (error) {
              setError(error, key, set, {
                // @ts-ignore -- TODO:  Fix the type of the store interface
                // current implementation has too many instances
                // where the types are casted using 'any'
                // makes it difficult to achieve static typing
                showSkeleton: false,
              });
            }

            setData(data, message, key, set, {
              ...transactionHistoryDefault,
              // @ts-ignore -- Same as above
              showSkeleton: false,
            });

            return response.data;
          } catch (error) {
            setError(error, key, set, {
              ...transactionHistoryDefault,
              // @ts-ignore
              showSkeleton: false,
            });
            return { error };
          }
        },

        getCompany: async (symbol) => {
          const key = 'company';
          setIsLoading(key, set);
          try {
            const companyResponse = await orderbook.getCompanyInfo(symbol);

            const { data, error, message } = companyResponse.data;

            if (error) {
              setError(error, key, set);
              throw new Error(error);
            }

            setData(data, message, key, set);
            return { data, message };
          } catch (error) {
            setError(error, key, set);
            return Promise.reject(new Error(error));
          }
        },

        getCorpActions: async (symbols) => {
          const key = 'corpActions';
          setIsLoading(key, set);
          try {
            const companyResponse = await securities.getAllCorpAction(symbols);

            const { data, error, message } = companyResponse.data;

            if (error) {
              setError(error, key, set);
              throw new Error(error);
            }

            setData(data, message, key, set);
            return { data, message };
          } catch (error) {
            setError(error, key, set);
            return { error };
          }
        },

        getExercise: async (stockCode) => {
          const key = 'exercise';
          setIsLoading(key, set);
          try {
            const response = await tradeAPI.getExercise(stockCode);

            if (!response.data) {
              const error = 'Attempt to post trade buy failed';
              setError(error, key, set);
              throw new Error(error);
            }
            const { data, message } = response.data;
            setData(data, message, key, set);
            return { data, message };
          } catch (error) {
            setError(error, key, set);
            return Promise.reject(new Error(error));
          }
        },

        checkRightTradeable: async () => {
          const key = 'rightTradeable';
          setIsLoading(key, set);
          try {
            const response = await tradeAPI.checkRightTradeable();

            if (!response.data) {
              const error = 'Attempt to post trade buy failed';
              setError(error, key, set);
              throw new Error(error);
            }
            const { data, message } = response.data;
            setData(data, message, key, set);
            return { data, message };
          } catch (error) {
            setError(error, key, set);
            return Promise.reject(new Error(error));
          }
        },

        getPortfolioDetail: async (stockCode) => {
          const key = 'portfolioDetail';
          const keyDetail = 'portfolioDetailDayTrade';

          setIsLoading(key, set, portfolioDetailDefault);

          try {
            const response = await tradeAPI.getPortfolioDetail(stockCode);

            if (!response.data) {
              const error = 'Attempt to get portfolio detail failed';
              setError(error, key, set, portfolioDetailDefault);
              throw new Error(error);
            }

            const { data, message } = response.data;
            const result = Transform.portfolioItem(data.result);
            const dayTrade = Transform.portfolioItem(data.day_trade);

            setData(result, message, key, set, portfolioDetailDefault);
            setData(dayTrade, message, keyDetail, set, portfolioDetailDefault);

            return {
              data: { result, day_trade: dayTrade },
              message,
            };
          } catch (error) {
            setError(error, key, set, portfolioDetailDefault);
            return { error };
          }
        },

        getCashOnHand: async () => {
          const key = 'cashOnHand';
          setIsLoading(key, set, cashOnHandDefault);
          try {
            const response = await tradeAPI.getCashOnHand();

            if (!response.data) {
              const error = 'Attempt to get cash on hand failed';
              setError(error, key, set, cashOnHandDefault);
              throw new Error(error);
            }
            const { data, message } = response.data;
            setData(data, message, key, set, cashOnHandDefault);
            return { data, message };
          } catch (error) {
            setError(error, key, set, cashOnHandDefault);
            return { error };
          }
        },

        // Live Portfolio - LivePrice
        updatePortfolioListItem: (stockCode, newData) => {
          if (!stockCode || !newData) return;

          const key = 'portfolio';
          const { price_latest } = newData;
          const portfolioData = get().portfolio?.data;
          if (!portfolioData) return;

          const persistedFormula = get().configFormula;
          if (!persistedFormula?.data) return;

          // Update portfolio list item (Table) - Regular
          const resultList: ListItem[] = portfolioData.result?.map(
            (item: ListItem) => {
              if (item.symbol === stockCode) {
                const updatedData = generateUpdatedPortfolioItem(
                  persistedFormula.data,
                  price_latest,
                  item.balance_lot,
                  item.amount_invested,
                );

                return {
                  ...item,
                  price_latest,
                  ...updatedData,
                };
              }
              return item;
            },
          );

          const dayTradeList: ListItem[] = portfolioData.day_trade?.map(
            (item: ListItem) => {
              if (item.symbol === stockCode) {
                const updatedData = generateUpdatedPortfolioItem(
                  persistedFormula.data,
                  price_latest,
                  item.balance_lot,
                  item.amount_invested,
                );

                return {
                  ...item,
                  price_latest,
                  ...updatedData,
                };
              }
              return item;
            },
          );

          // Update portfolio summary (Header) - Live Price
          // Update portfolio summary (Header)
          const { unrealised_profitloss, gain, equity, ratio, market_value } =
            generateUpdatedPortfolioHeader(
              persistedFormula.data,
              resultList,
              dayTradeList,
              portfolioData.tradingbalance,
              portfolioData.amount_invested,
              portfolioData.amount_allocated,
              portfolioData.debt_total,
            );

          setData(
            {
              ...portfolioData,
              result: resultList,
              day_trade: dayTradeList,
              unrealised_profitloss,
              gain,
              equity,
              debt_ratio: ratio,
              debt_market_value: market_value,
            },
            'update-portfolio-liveprice',
            key,
            set,
            portfolioDefault,
          );
        },
        // End Live Portfolio - LivePrice

        // Live Portfolio - New WS
        updatePortfolioSummary: (newData) => {
          if (!newData) return;

          const key = 'portfolio';
          const portfolioData = get().portfolio?.data;
          if (!portfolioData) return;

          setData(
            {
              ...portfolioData,
              ...newData,
            },
            'update-portfolio-data-ws',
            key,
            set,
            portfolioDefault,
          );
        },

        updatePortfolioList: (newData) => {
          if (!newData) return;

          const key = 'portfolio';
          const portfolioData = get().portfolio?.data;
          if (!portfolioData) return;

          const resultList = newData.result.map((item) => {
            const oldItem = portfolioData.result?.find(
              (res) => res.symbol === item.symbol,
            );

            if (!oldItem) return item;

            return {
              ...oldItem,
              ...item,
            };
          });

          const dayTradeList = newData.day_trade.map((item) => {
            const oldItem = portfolioData.day_trade?.find(
              (res) => res.symbol === item.symbol,
            );

            if (!oldItem) return item;

            return {
              ...oldItem,
              ...item,
            };
          });

          setData(
            {
              ...portfolioData,
              ...newData,
              result: resultList,
              day_trade: dayTradeList,
            },
            'update-portfolio-data-ws',
            key,
            set,
            portfolioDefault,
          );
        },

        updatePortfolioItem: (stockCode, newData, isDayTrade = false) => {
          if (!stockCode || !newData) return;

          const key = 'portfolio';
          const portfolioDetailData = get().portfolio?.data;
          if (!portfolioDetailData || !newData) return;

          const portfolioList = isDayTrade
            ? portfolioDetailData.day_trade
            : portfolioDetailData.result;

          if (!portfolioList) return;

          // Update portfolio detail if exist
          const resultList: ListItem[] = portfolioList.map((item: ListItem) => {
            if (item.symbol === stockCode) {
              return {
                ...item,
                ...newData,
              };
            }
            return item;
          });

          // Add new portfolio detail if not exist
          const isPortfolioExist = portfolioList.find(
            (item: ListItem) => item.symbol === stockCode,
          );

          if (!isPortfolioExist && resultList) {
            resultList.push(newData);

            resultList.sort((a: ListItem, b: ListItem) => {
              const symbolA = a.symbol.toUpperCase();
              const symbolB = b.symbol.toUpperCase();

              if (symbolA < symbolB) {
                return -1;
              }
              if (symbolA > symbolB) {
                return 1;
              }

              return 0;
            });
          }

          const updatedData = {
            ...portfolioDetailData,
            ...(isDayTrade
              ? { day_trade: resultList }
              : { result: resultList }),
          };

          setData(updatedData, 'update-portfolio-item-ws', key, set);

          // update portfolioDetail if exist
          const portfolioDetail = isDayTrade
            ? get().portfolioDetailDayTrade?.data
            : get().portfolioDetail?.data;

          if (!portfolioDetail) return;

          if (portfolioDetail.symbol === stockCode) {
            const keyDetail = isDayTrade
              ? 'portfolioDetailDayTrade'
              : 'portfolioDetail';

            setData(
              {
                ...portfolioDetail,
                ...newData,
              },
              'update-portfolio-detail-ws',
              keyDetail,
              set,
            );
          }
        },

        deletePortfolioItem: (stockCode, isDayTrade = false) => {
          if (!stockCode) return;

          const key = 'portfolio';
          const portfolioData = get().portfolio?.data;
          if (!portfolioData) return;

          const portfolioList = isDayTrade
            ? get().portfolio?.data?.day_trade
            : get().portfolio?.data?.result;

          if (!portfolioList) return;

          const resultList: ListItem[] = portfolioList.filter(
            (item: ListItem) => item.symbol !== stockCode,
          );

          const updatedData = {
            ...portfolioData,
            ...(isDayTrade
              ? { day_trade: resultList }
              : { result: resultList }),
          };

          setData(updatedData, 'update-portfolio-item-ws', key, set);
        },

        updateOrderList: (newData) => {
          if (!newData) return;

          const key = 'orderHistory';
          const orderHistoryData = get().orderHistory?.data;

          if (!orderHistoryData) return;

          const orderList = newData.map((item) => {
            const oldItem = orderHistoryData.find(
              (res) => res.orderid === item.orderid,
            );

            if (!oldItem) return item;

            return {
              ...oldItem,
              ...item,
            };
          });

          if (orderList?.length === 0) return;

          setData(orderList, 'update-order-list-ws', key, set);
          orderTableStore.getState().searchAndFilter(orderList);
        },

        updateOrderListItem: (orderId, newData) => {
          if (!orderId || !newData) return;

          const key = 'orderHistory';
          const orderHistoryData = get().orderHistory?.data;
          const orderHistoryLoading = get().orderHistory?.isLoading;
          if (!orderHistoryData) return;

          // Update order detail if exist
          const resultList = orderHistoryData.map((item: OrderItem) => {
            if (item.orderid === orderId) {
              return {
                ...item,
                ...newData,
              };
            }
            return item;
          });

          // Add new order detail if not exist
          const isOrderExist = orderHistoryData.find(
            (item: OrderItem) => item.orderid === orderId,
          );

          // only update order list if order list is not empty and not loading
          if (orderHistoryData?.length === 0 && orderHistoryLoading) return;

          if (!isOrderExist) {
            resultList.unshift(newData);
          }

          setData(
            resultList,
            'update-order-item-ws',
            key,
            set,
            orderHistoryDefault,
          );
          orderTableStore.getState().searchAndFilter(resultList);
        },

        deleteOrderListItem: (orderId) => {
          if (!orderId) return;

          const key = 'orderHistory';
          const orderHistoryData = get().orderHistory?.data;
          if (!orderHistoryData) return;

          const resultList = orderHistoryData.filter(
            (item: OrderItem) => item.orderid !== orderId,
          );

          setData(resultList, 'delete-order-item-ws', key, set);
          orderTableStore.getState().searchAndFilter(resultList);
        },
        // End Live Portfolio - New WS

        checkStockTradable: async (stockCode) => {
          const key = 'stockTradable';
          setIsLoading(key, set);
          try {
            const response = await tradeAPI.checkStockTradable(stockCode);

            if (!response.data) {
              const error = 'Attempt to check stock tradable failed';
              setError(error, key, set);
              throw new Error(error);
            }
            const { data, message } = response.data;
            const tradableData = data[0];

            setData(tradableData, message, key, set);
            return { data: tradableData, message };
          } catch (error) {
            setError(error, key, set);
            return Promise.reject(new Error(error));
          }
        },
      })),
      {
        name: 'zustandStockbitFormula',
        getStorage: () => localStorage,
        partialize: (state) => ({
          configFormula: state.configFormula,
        }),
      },
    ),
  ),
);

export default tradingDataStore;
